import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Markdown from 'react-markdown';
import { API_BASE_URL } from './config';
import { DatasetsView, EmbedTokensView, TemplatesView } from './ReportsGovernance';

// ─── Styles ────────────────────────────────────────────────────────────────
const containerStyle = { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' };
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexWrap: 'wrap', gap: '8px' };
const bodyStyle = { flex: 1, overflow: 'auto', padding: '20px' };
const btnPrimary = { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' };
const btnDanger = { background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' };
const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const cardStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px' };
const tabStyle = (active) => ({ padding: '8px 16px', cursor: 'pointer', borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent', color: active ? 'var(--accent-color)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400, fontSize: '13px', background: 'transparent', border: 'none', borderBottomWidth: '2px', borderBottomStyle: 'solid' });
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContent = { background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', width: '480px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', border: '1px solid var(--border-color)' };

const VIZ_TYPES = [
  { key: 'table', label: 'Table' },
  { key: 'bar', label: 'Bar' },
  { key: 'line', label: 'Line' },
  { key: 'pie', label: 'Pie' },
  { key: 'kpi', label: 'KPI Tile' },
  { key: 'pivot', label: 'Pivot' },
  { key: 'heatmap', label: 'Heatmap' },
  { key: 'scatter', label: 'Scatter' },
  { key: 'gauge', label: 'Gauge' },
  { key: 'sparkline', label: 'Sparkline' },
];

const CF_OPS = [
  { key: 'gt', label: '>' },
  { key: 'gte', label: '≥' },
  { key: 'lt', label: '<' },
  { key: 'lte', label: '≤' },
  { key: 'eq', label: '=' },
  { key: 'ne', label: '≠' },
  { key: 'between', label: 'between' },
];

const CF_COLORS = [
  { key: '#fee2e2', label: 'Red',    text: '#991b1b' },
  { key: '#fef3c7', label: 'Amber',  text: '#92400e' },
  { key: '#dcfce7', label: 'Green',  text: '#14532d' },
  { key: '#dbeafe', label: 'Blue',   text: '#1e3a8a' },
  { key: '#ede9fe', label: 'Purple', text: '#4c1d95' },
];

// Evaluate a list of conditional-format rules; first match wins. Returns hex color or null.
function evalConditionalRules(columnLabel, value, rules) {
  if (!rules || rules.length === 0 || value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  for (const rule of rules) {
    if (rule.column && rule.column !== columnLabel) continue;
    const t = rule.threshold;
    const op = rule.op || 'gt';
    if (op === 'between' && Array.isArray(t) && t.length === 2) {
      if (num >= Number(t[0]) && num <= Number(t[1])) return rule.color;
      continue;
    }
    const tn = Number(t);
    if (!Number.isFinite(tn)) continue;
    if (op === 'gt' && num > tn) return rule.color;
    if (op === 'gte' && num >= tn) return rule.color;
    if (op === 'lt' && num < tn) return rule.color;
    if (op === 'lte' && num <= tn) return rule.color;
    if (op === 'eq' && num === tn) return rule.color;
    if (op === 'ne' && num !== tn) return rule.color;
  }
  return null;
}

const AGG_OPTIONS = [
  { key: 'count', label: 'Count' },
  { key: 'count_distinct', label: 'Count Distinct' },
  { key: 'sum', label: 'Sum' },
  { key: 'avg', label: 'Average' },
  { key: 'min', label: 'Min' },
  { key: 'max', label: 'Max' },
];

const FILTER_OPS = [
  { key: 'eq', label: '=' },
  { key: 'ne', label: '≠' },
  { key: 'gt', label: '>' },
  { key: 'gte', label: '≥' },
  { key: 'lt', label: '<' },
  { key: 'lte', label: '≤' },
  { key: 'in', label: 'in' },
  { key: 'not_in', label: 'not in' },
  { key: 'contains', label: 'contains' },
  { key: 'is_null', label: 'is empty' },
  { key: 'is_not_null', label: 'is not empty' },
];

const BUCKETS = [
  { key: '', label: 'No bucket' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
];

const CHART_PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899', '#f97316', '#14b8a6'];

// ─── Helpers ───────────────────────────────────────────────────────────────
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function formatCellValue(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'boolean') return v ? '✓' : '';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2);
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 57) + '…';
  return String(v);
}

// ─── Chart components (SVG) ────────────────────────────────────────────────
function BarChart({ result, height = 260 }) {
  const dimCol = result.columns.find(c => c.role === 'dimension');
  const measCols = result.columns.filter(c => c.role === 'measure');
  if (!dimCol || measCols.length === 0 || result.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>Need 1 dimension + ≥1 measure to render bars.</div>;
  }
  const maxVal = Math.max(1, ...result.rows.flatMap(r => measCols.map(m => Number(r[m.label] || 0))));
  const barWidth = Math.min(50, Math.max(20, 600 / Math.max(1, result.rows.length * measCols.length)));
  const groupGap = 14;
  const barGap = 4;
  const groupWidth = measCols.length * (barWidth + barGap);
  const labelArea = 30;
  const chartHeight = height - labelArea;
  return (
    <div style={{ overflowX: 'auto', padding: '10px 0' }}>
      <svg width={Math.max(640, result.rows.length * (groupWidth + groupGap) + 60)} height={height}>
        {result.rows.map((row, ri) => {
          const groupX = 40 + ri * (groupWidth + groupGap);
          return (
            <g key={ri}>
              {measCols.map((m, mi) => {
                const v = Number(row[m.label] || 0);
                const h = (v / maxVal) * chartHeight;
                const x = groupX + mi * (barWidth + barGap);
                const y = chartHeight - h + 5;
                return (
                  <g key={mi}>
                    <rect x={x} y={y} width={barWidth} height={h} fill={CHART_PALETTE[mi % CHART_PALETTE.length]} rx={3} />
                    <text x={x + barWidth / 2} y={y - 3} textAnchor="middle" fontSize="10" fill="var(--text-secondary)">{formatCellValue(v)}</text>
                  </g>
                );
              })}
              <text x={groupX + groupWidth / 2} y={chartHeight + 22} textAnchor="middle" fontSize="11" fill="var(--text-primary)">{formatCellValue(row[dimCol.label])}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '6px', fontSize: '11px' }}>
        {measCols.map((m, mi) => (
          <span key={m.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, background: CHART_PALETTE[mi % CHART_PALETTE.length], borderRadius: 2 }} /> {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function LineChart({ result, height = 260 }) {
  const dimCol = result.columns.find(c => c.role === 'dimension');
  const measCols = result.columns.filter(c => c.role === 'measure');
  if (!dimCol || measCols.length === 0 || result.rows.length < 2) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>Need 1 dimension + ≥1 measure (and ≥2 rows) to render a line.</div>;
  }
  const width = Math.max(600, result.rows.length * 60);
  const padding = { top: 16, right: 20, bottom: 30, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const maxVal = Math.max(1, ...result.rows.flatMap(r => measCols.map(m => Number(r[m.label] || 0))));
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={width} height={height}>
        {/* y-axis grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <g key={t}>
            <line x1={padding.left} x2={width - padding.right} y1={padding.top + chartH * (1 - t)} y2={padding.top + chartH * (1 - t)} stroke="var(--border-color)" strokeWidth={1} strokeDasharray="2,2" />
            <text x={padding.left - 4} y={padding.top + chartH * (1 - t) + 3} textAnchor="end" fontSize="9" fill="var(--text-tertiary)">{(maxVal * t).toFixed(0)}</text>
          </g>
        ))}
        {measCols.map((m, mi) => {
          const points = result.rows.map((row, ri) => {
            const x = padding.left + (result.rows.length === 1 ? chartW / 2 : (ri / (result.rows.length - 1)) * chartW);
            const v = Number(row[m.label] || 0);
            const y = padding.top + chartH - (v / maxVal) * chartH;
            return [x, y, v];
          });
          const pathD = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
          const color = CHART_PALETTE[mi % CHART_PALETTE.length];
          return (
            <g key={m.label}>
              <path d={pathD} fill="none" stroke={color} strokeWidth={2} />
              {points.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={3} fill={color} />
              ))}
            </g>
          );
        })}
        {/* x-axis labels */}
        {result.rows.map((row, ri) => {
          const x = padding.left + (result.rows.length === 1 ? chartW / 2 : (ri / (result.rows.length - 1)) * chartW);
          return <text key={ri} x={x} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--text-secondary)">{formatCellValue(row[dimCol.label])}</text>;
        })}
      </svg>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '6px', fontSize: '11px' }}>
        {measCols.map((m, mi) => (
          <span key={m.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 14, height: 2, background: CHART_PALETTE[mi % CHART_PALETTE.length] }} /> {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function PieChart({ result, size = 240 }) {
  const dimCol = result.columns.find(c => c.role === 'dimension');
  const measCol = result.columns.find(c => c.role === 'measure');
  if (!dimCol || !measCol || result.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>Need 1 dimension + 1 measure for pie.</div>;
  }
  const total = result.rows.reduce((s, r) => s + Number(r[measCol.label] || 0), 0);
  if (total === 0) return <div style={{ padding: '20px', color: 'var(--text-tertiary)' }}>No data.</div>;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 10;
  let startAngle = -Math.PI / 2;
  const slices = result.rows.map((row, ri) => {
    const v = Number(row[measCol.label] || 0);
    const portion = v / total;
    const angle = portion * Math.PI * 2;
    const endAngle = startAngle + angle;
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const slice = { row, ri, path, color: CHART_PALETTE[ri % CHART_PALETTE.length], v, portion };
    startAngle = endAngle;
    return slice;
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg width={size} height={size}>
        {slices.map(s => (
          <path key={s.ri} d={s.path} fill={s.color} stroke="var(--bg-primary)" strokeWidth={2}>
            <title>{`${formatCellValue(s.row[dimCol.label])}: ${formatCellValue(s.v)} (${(s.portion * 100).toFixed(1)}%)`}</title>
          </path>
        ))}
      </svg>
      <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {slices.map(s => (
          <span key={s.ri} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, background: s.color, borderRadius: 2 }} />
            <span>{formatCellValue(s.row[dimCol.label])}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>· {formatCellValue(s.v)} ({(s.portion * 100).toFixed(1)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function KpiTile({ result, vizConfig }) {
  const measCol = result.columns.find(c => c.role === 'measure');
  if (!measCol || result.rows.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No data.</div>;
  }
  const v = result.rows.reduce((s, r) => s + Number(r[measCol.label] || 0), 0);
  const compare = result.compare;
  const delta = compare?.delta?.[measCol.label];
  const deltaPct = compare?.delta_pct?.[measCol.label];
  const arrow = delta == null ? '' : (delta > 0 ? '▲' : (delta < 0 ? '▼' : '■'));
  const dColor = delta == null ? 'var(--text-tertiary)' : (delta > 0 ? '#16a34a' : (delta < 0 ? '#dc2626' : 'var(--text-tertiary)'));
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{measCol.label}</div>
      <div style={{ fontSize: '44px', fontWeight: 700, color: 'var(--accent-color)', marginTop: '6px' }}>{formatCellValue(v)}</div>
      {compare && delta != null && (
        <div style={{ fontSize: '13px', color: dColor, marginTop: '6px', fontWeight: 600 }}>
          {arrow} {formatCellValue(delta)}
          {deltaPct != null && <span style={{ marginLeft: 4 }}>({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%)</span>}
          <div style={{ fontSize: '10px', fontWeight: 400, color: 'var(--text-tertiary)', marginTop: 2 }}>vs previous period</div>
        </div>
      )}
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '6px' }}>{result.rows.length} {result.rows.length === 1 ? 'group' : 'groups'} · {result.duration_ms != null ? `${result.duration_ms}ms` : ''}</div>
    </div>
  );
}

function TableView({ result, vizConfig }) {
  if (!result.rows || result.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>No rows.</div>;
  }
  const rules = vizConfig?.conditional_format || [];
  return (
    <div style={{ overflowX: 'auto', maxHeight: 400 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)' }}>
          <tr>
            {result.columns.map(c => (
              <th key={c.key} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {c.label}
                {c.role === 'measure' && <span style={{ color: 'var(--text-tertiary)', marginLeft: 4 }}>Σ</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid var(--border-color)' }}>
              {result.columns.map(c => {
                const v = row[c.key];
                const bg = evalConditionalRules(c.key, v, rules);
                return (
                  <td key={c.key} style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text-primary)', background: bg || 'transparent' }}>{formatCellValue(v)}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Phase 3: Pivot, Heatmap, Scatter, Gauge, Sparkline ────────────────────
function PivotTable({ result, vizConfig }) {
  const pv = result.pivot;
  if (!pv || !pv.rows || pv.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
      Pivot needs ≥2 dimensions and a measure, plus a <code>pivot.col_key</code> in the query (set via Pivot column dropdown).
    </div>;
  }
  const rules = vizConfig?.conditional_format || [];
  const rowLabels = pv.row_dim_labels || [];
  const colKeys = pv.col_keys || [];
  const measureLabel = pv.measure_label || '';
  return (
    <div style={{ overflowX: 'auto', maxHeight: 460 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)' }}>
          <tr>
            {rowLabels.map(rl => (
              <th key={rl} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{rl}</th>
            ))}
            {colKeys.map(ck => (
              <th key={ck} style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{ck}</th>
            ))}
            <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid var(--border-color)', color: 'var(--accent-color)', fontWeight: 700 }}>Σ {measureLabel}</th>
          </tr>
        </thead>
        <tbody>
          {pv.rows.map((prow, ri) => (
            <tr key={ri} style={{ borderBottom: '1px solid var(--border-color)' }}>
              {rowLabels.map(rl => (
                <td key={rl} style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{formatCellValue(prow[rl])}</td>
              ))}
              {colKeys.map(ck => {
                const v = prow[ck];
                const bg = evalConditionalRules(ck, v, rules) || evalConditionalRules(measureLabel, v, rules);
                return (
                  <td key={ck} style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: bg || 'transparent', color: 'var(--text-primary)' }}>{formatCellValue(v)}</td>
                );
              })}
              <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCellValue(prow.__row_total__)}</td>
            </tr>
          ))}
          <tr style={{ background: 'rgba(99,102,241,0.08)' }}>
            {rowLabels.map((_, idx) => (
              <td key={idx} style={{ padding: '6px 10px', fontWeight: 600 }}>{idx === 0 ? 'Total' : ''}</td>
            ))}
            {colKeys.map(ck => (
              <td key={ck} style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatCellValue(pv.col_totals?.[ck])}</td>
            ))}
            <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--accent-color)' }}>{formatCellValue(pv.grand_total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Heatmap({ result }) {
  // Heatmap = pivot grid colored by intensity. Reuses pivot output if present;
  // otherwise tries to build one from the first 2 dimensions + first measure.
  const pv = result.pivot;
  if (!pv || !pv.rows || pv.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
      Heatmap needs 2 dimensions + 1 measure (mark one dimension as <b>Pivot column</b>).
    </div>;
  }
  const rowLabels = pv.row_dim_labels || [];
  const colKeys = pv.col_keys || [];
  const all = pv.rows.flatMap(r => colKeys.map(ck => Number(r[ck])).filter(Number.isFinite));
  const max = Math.max(1, ...all);
  const min = Math.min(0, ...all);
  const colorAt = (v) => {
    if (v == null || !Number.isFinite(Number(v))) return 'transparent';
    const t = Math.max(0, Math.min(1, (Number(v) - min) / (max - min || 1)));
    // Indigo → bright orange ramp
    const r = Math.round(99 + (245 - 99) * t);
    const g = Math.round(102 + (158 - 102) * t);
    const b = Math.round(241 + (11 - 241) * t);
    return `rgb(${r},${g},${b})`;
  };
  return (
    <div style={{ overflowX: 'auto', maxHeight: 460 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead><tr>
          {rowLabels.map(rl => <th key={rl} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-secondary)' }}>{rl}</th>)}
          {colKeys.map(ck => <th key={ck} style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: 600 }}>{ck}</th>)}
        </tr></thead>
        <tbody>
          {pv.rows.map((prow, ri) => (
            <tr key={ri}>
              {rowLabels.map(rl => <td key={rl} style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--text-primary)', fontWeight: 500 }}>{formatCellValue(prow[rl])}</td>)}
              {colKeys.map(ck => {
                const v = prow[ck];
                const bg = colorAt(v);
                const intensity = v == null ? 0 : (Number(v) - min) / (max - min || 1);
                return <td key={ck} style={{ padding: '8px 10px', textAlign: 'center', background: bg, color: intensity > 0.55 ? '#fff' : 'var(--text-primary)', minWidth: 56, fontVariantNumeric: 'tabular-nums', fontSize: '11px' }}>{formatCellValue(v)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 10, color: 'var(--text-tertiary)' }}>
        <span>{formatCellValue(min)}</span>
        <div style={{ width: 160, height: 8, background: 'linear-gradient(to right, rgb(99,102,241), rgb(245,158,11))', borderRadius: 4 }} />
        <span>{formatCellValue(max)}</span>
      </div>
    </div>
  );
}

function ScatterChart({ result, height = 280 }) {
  const measCols = result.columns.filter(c => c.role === 'measure');
  if (measCols.length < 2 || result.rows.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--text-tertiary)', textAlign: 'center' }}>Scatter needs ≥2 measures (first is X, second is Y) and ≥1 row.</div>;
  }
  const dimCol = result.columns.find(c => c.role === 'dimension');
  const xCol = measCols[0];
  const yCol = measCols[1];
  const xs = result.rows.map(r => Number(r[xCol.label] || 0));
  const ys = result.rows.map(r => Number(r[yCol.label] || 0));
  const xMin = Math.min(0, ...xs); const xMax = Math.max(1, ...xs);
  const yMin = Math.min(0, ...ys); const yMax = Math.max(1, ...ys);
  const width = 560;
  const padding = { top: 16, right: 24, bottom: 36, left: 50 };
  const w = width - padding.left - padding.right;
  const h = height - padding.top - padding.bottom;
  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={width} height={height}>
        {[0, 0.25, 0.5, 0.75, 1].map(t => (
          <g key={t}>
            <line x1={padding.left} x2={width - padding.right} y1={padding.top + h * (1 - t)} y2={padding.top + h * (1 - t)} stroke="var(--border-color)" strokeWidth={1} strokeDasharray="2,2" />
            <text x={padding.left - 4} y={padding.top + h * (1 - t) + 3} textAnchor="end" fontSize="9" fill="var(--text-tertiary)">{(yMin + (yMax - yMin) * t).toFixed(0)}</text>
          </g>
        ))}
        {result.rows.map((row, i) => {
          const xv = Number(row[xCol.label] || 0);
          const yv = Number(row[yCol.label] || 0);
          const cx = padding.left + ((xv - xMin) / (xMax - xMin || 1)) * w;
          const cy = padding.top + h - ((yv - yMin) / (yMax - yMin || 1)) * h;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={5} fill={CHART_PALETTE[i % CHART_PALETTE.length]} fillOpacity={0.7} stroke="#fff" strokeWidth={1}>
                <title>{`${dimCol ? formatCellValue(row[dimCol.label]) + ' · ' : ''}${xCol.label}=${formatCellValue(xv)}, ${yCol.label}=${formatCellValue(yv)}`}</title>
              </circle>
            </g>
          );
        })}
        <text x={width / 2} y={height - 8} textAnchor="middle" fontSize="11" fill="var(--text-secondary)">{xCol.label}</text>
        <text x={14} y={height / 2} textAnchor="middle" fontSize="11" fill="var(--text-secondary)" transform={`rotate(-90 14 ${height / 2})`}>{yCol.label}</text>
      </svg>
    </div>
  );
}

function GaugeChart({ result, vizConfig }) {
  const measCol = result.columns.find(c => c.role === 'measure');
  if (!measCol || result.rows.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Gauge needs 1 measure.</div>;
  }
  const v = result.rows.reduce((s, r) => s + Number(r[measCol.label] || 0), 0);
  const cfg = vizConfig?.gauge || {};
  const min = Number(cfg.min ?? 0);
  const max = Number(cfg.max ?? Math.max(100, v * 1.2));
  const target = cfg.target != null ? Number(cfg.target) : null;
  const dangerAt = cfg.danger_at != null ? Number(cfg.danger_at) : null;
  const warnAt = cfg.warn_at != null ? Number(cfg.warn_at) : null;
  const t = Math.max(0, Math.min(1, (v - min) / (max - min || 1)));
  // Half-circle gauge: angle 0 (left, 180°) to π (right, 0°). Sweep from -π → 0.
  const cx = 140, cy = 130, r = 100;
  const angle = -Math.PI + Math.PI * t;
  const px = cx + r * Math.cos(angle); const py = cy + r * Math.sin(angle);
  const arcPath = (a0, a1, color) => {
    const x0 = cx + r * Math.cos(a0); const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1); const y1 = cy + r * Math.sin(a1);
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    return <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} fill="none" stroke={color} strokeWidth={18} strokeLinecap="round" />;
  };
  const cur = (a, c) => arcPath(-Math.PI, a, c);
  let stateColor = '#10b981';
  if (dangerAt != null && v >= dangerAt) stateColor = '#ef4444';
  else if (warnAt != null && v >= warnAt) stateColor = '#f59e0b';
  return (
    <div style={{ padding: '20px 16px', textAlign: 'center' }}>
      <svg width={280} height={170}>
        {arcPath(-Math.PI, 0, '#e5e7eb')}
        {cur(angle, stateColor)}
        {target != null && (() => {
          const tt = Math.max(0, Math.min(1, (target - min) / (max - min || 1)));
          const ta = -Math.PI + Math.PI * tt;
          const tx = cx + (r + 8) * Math.cos(ta); const ty = cy + (r + 8) * Math.sin(ta);
          const ix = cx + (r - 8) * Math.cos(ta); const iy = cy + (r - 8) * Math.sin(ta);
          return <line x1={ix} y1={iy} x2={tx} y2={ty} stroke="var(--text-primary)" strokeWidth={2} />;
        })()}
        <circle cx={cx} cy={cy} r={6} fill={stateColor} />
        <line x1={cx} y1={cy} x2={px} y2={py} stroke={stateColor} strokeWidth={3} />
      </svg>
      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{measCol.label}</div>
      <div style={{ fontSize: '32px', fontWeight: 700, color: stateColor, marginTop: '4px' }}>{formatCellValue(v)}</div>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: 4 }}>
        Range: {min}–{max}{target != null ? ` · target ${target}` : ''}{warnAt != null ? ` · warn ≥ ${warnAt}` : ''}{dangerAt != null ? ` · danger ≥ ${dangerAt}` : ''}
      </div>
    </div>
  );
}

function Sparkline({ result, height = 60 }) {
  const measCol = result.columns.find(c => c.role === 'measure');
  if (!measCol || result.rows.length < 2) {
    return <div style={{ padding: '12px', color: 'var(--text-tertiary)', textAlign: 'center', fontSize: '11px' }}>Sparkline needs 1 measure and ≥2 rows.</div>;
  }
  const values = result.rows.map(r => Number(r[measCol.label] || 0));
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const width = 320;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / (max - min || 1)) * height;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const trendUp = last > first;
  return (
    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <svg width={width} height={height + 4}>
        <path d={`${path} L ${width} ${height} L 0 ${height} Z`} fill={trendUp ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)'} />
        <path d={path} fill="none" stroke={trendUp ? '#10b981' : '#ef4444'} strokeWidth={2} />
        <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r={3} fill={trendUp ? '#10b981' : '#ef4444'} />
      </svg>
      <div>
        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{measCol.label}</div>
        <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)' }}>{formatCellValue(last)}</div>
        <div style={{ fontSize: '10px', color: trendUp ? '#10b981' : '#ef4444' }}>
          {trendUp ? '▲' : '▼'} from {formatCellValue(first)}
        </div>
      </div>
    </div>
  );
}

function VizRender({ result, vizType, vizConfig }) {
  if (!result || !result.columns) return null;
  if (result.error) return <div style={{ padding: '12px', color: '#ef4444' }}>Error: {result.error}</div>;
  switch (vizType) {
    case 'bar':       return <BarChart result={result} />;
    case 'line':      return <LineChart result={result} />;
    case 'pie':       return <PieChart result={result} />;
    case 'kpi':       return <KpiTile result={result} vizConfig={vizConfig} />;
    case 'pivot':     return <PivotTable result={result} vizConfig={vizConfig} />;
    case 'heatmap':   return <Heatmap result={result} />;
    case 'scatter':   return <ScatterChart result={result} />;
    case 'gauge':     return <GaugeChart result={result} vizConfig={vizConfig} />;
    case 'sparkline': return <Sparkline result={result} />;
    case 'table':
    default:          return <TableView result={result} vizConfig={vizConfig} />;
  }
}

// ─── Conditional formatting editor (rules list) ───────────────────────────
function ConditionalFormattingEditor({ rules, columnLabels, onChange }) {
  const addRule = () => onChange([...rules, { column: columnLabels[0] || '', op: 'gt', threshold: 0, color: CF_COLORS[0].key }]);
  const updateRule = (idx, patch) => onChange(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRule = (idx) => onChange(rules.filter((_, i) => i !== idx));
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={labelStyle}>Conditional formatting</label>
        <button onClick={addRule} style={{ ...btnSecondary, fontSize: '10px', padding: '2px 8px' }}>+ Rule</button>
      </div>
      {rules.length === 0 && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>No rules — cells render with the default theme.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
        {rules.map((r, idx) => (
          <div key={idx} style={{ padding: '6px', border: '1px solid var(--border-color)', borderRadius: 5, background: 'var(--bg-input)' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <select value={r.column || ''} onChange={e => updateRule(idx, { column: e.target.value })} style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 2 }}>
                <option value="">(any column)</option>
                {columnLabels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <select value={r.op || 'gt'} onChange={e => updateRule(idx, { op: e.target.value, threshold: e.target.value === 'between' ? [0, 100] : 0 })} style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 1 }}>
                {CF_OPS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <button onClick={() => removeRule(idx)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {r.op === 'between' ? (
                <>
                  <input type="number" value={Array.isArray(r.threshold) ? r.threshold[0] : 0}
                    onChange={e => updateRule(idx, { threshold: [Number(e.target.value), Array.isArray(r.threshold) ? r.threshold[1] : 0] })}
                    style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 1 }} placeholder="min" />
                  <input type="number" value={Array.isArray(r.threshold) ? r.threshold[1] : 0}
                    onChange={e => updateRule(idx, { threshold: [Array.isArray(r.threshold) ? r.threshold[0] : 0, Number(e.target.value)] })}
                    style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 1 }} placeholder="max" />
                </>
              ) : (
                <input type="number" value={r.threshold ?? 0} onChange={e => updateRule(idx, { threshold: Number(e.target.value) })} style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 2 }} placeholder="threshold" />
              )}
              <select value={r.color || CF_COLORS[0].key} onChange={e => updateRule(idx, { color: e.target.value })} style={{ ...inputStyle, padding: '3px 6px', fontSize: '10px', flex: 1, background: r.color || CF_COLORS[0].key, color: '#111' }}>
                {CF_COLORS.map(c => <option key={c.key} value={c.key} style={{ background: c.key }}>{c.label}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Report Editor (modal-style page) ──────────────────────────────────────
function ReportEditor({ workspaceId, datasets, report, onClose, onSaved }) {
  const isEditing = !!report;
  const [name, setName] = useState(report?.name || '');
  const [description, setDescription] = useState(report?.description || '');
  const [datasetSlug, setDatasetSlug] = useState(report?.dataset_slug || (datasets[0]?.slug || ''));
  const [vizType, setVizType] = useState(report?.viz_type || 'table');
  const [columns, setColumns] = useState(report?.query_json?.columns || []);
  const [filters, setFilters] = useState(report?.query_json?.filters || []);
  const [orderBy, setOrderBy] = useState(report?.query_json?.order_by || []);
  const [limit, setLimit] = useState(report?.query_json?.limit || 100);
  const [pivotColKey, setPivotColKey] = useState(report?.query_json?.pivot?.col_key || '');
  const [compareEnabled, setCompareEnabled] = useState(report?.query_json?.compare === 'previous_period');
  const [vizConfig, setVizConfig] = useState(report?.viz_config_json || {});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const dataset = datasets.find(d => d.slug === datasetSlug);
  const availableColumns = dataset?.columns || [];

  const buildQuery = useCallback(() => {
    const q = {
      columns,
      filters: filters.filter(f => f.key && f.op),
      order_by: orderBy,
      limit: Number(limit) || 100,
    };
    if ((vizType === 'pivot' || vizType === 'heatmap') && pivotColKey) {
      q.pivot = { col_key: pivotColKey };
    }
    if (compareEnabled && vizType === 'kpi') {
      q.compare = 'previous_period';
    }
    return q;
  }, [columns, filters, orderBy, limit, vizType, pivotColKey, compareEnabled]);

  const runPreview = useCallback(async () => {
    if (!datasetSlug) return;
    setPreviewLoading(true);
    setError('');
    try {
      const url = `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/datasets/${datasetSlug}/preview`;
      const r = await axios.post(url, { query: buildQuery() }, getAuthHeaders());
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [workspaceId, datasetSlug, buildQuery]);

  // Auto-rerun preview whenever the query changes (debounced 300ms)
  const querySignature = JSON.stringify({ datasetSlug, columns, filters, orderBy, limit, pivotColKey, compareEnabled, vizType });
  useEffect(() => {
    const handle = setTimeout(() => { runPreview(); }, 300);
    return () => clearTimeout(handle);
  }, [querySignature]);

  const addColumn = (col, role) => {
    const def = { key: col.key, role, label: role === 'measure' ? `${'count'}(${col.key})` : col.label };
    if (role === 'measure') def.agg = 'count';
    setColumns(c => [...c, def]);
  };

  const updateColumn = (idx, patch) => {
    setColumns(c => c.map((col, i) => i === idx ? { ...col, ...patch } : col));
  };

  const removeColumn = (idx) => setColumns(c => c.filter((_, i) => i !== idx));

  const addFilter = () => setFilters(f => [...f, { key: availableColumns[0]?.key || '', op: 'eq', value: '' }]);
  const updateFilter = (idx, patch) => setFilters(f => f.map((flt, i) => i === idx ? { ...flt, ...patch } : flt));
  const removeFilter = (idx) => setFilters(f => f.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!datasetSlug) { setError('Dataset is required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = {
        name: name.trim(),
        description: description || null,
        dataset_slug: datasetSlug,
        viz_type: vizType,
        query_json: buildQuery(),
        viz_config_json: vizConfig || {},
      };
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${report.id}`,
          { name: body.name, description: body.description, viz_type: body.viz_type, query_json: body.query_json, viz_config_json: body.viz_config_json },
          getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports`, body, getAuthHeaders());
      }
      onSaved();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onClose} style={btnSecondary}>← Back</button>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Report name"
            style={{ ...inputStyle, width: '300px', fontSize: '15px', fontWeight: 600 }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={runPreview} style={btnSecondary} disabled={previewLoading}>{previewLoading ? 'Running…' : '▶ Preview'}</button>
          <button onClick={handleSave} style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : (isEditing ? 'Save changes' : 'Create report')}</button>
        </div>
      </div>
      {error && <div style={{ padding: '8px 20px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px' }}>{error}</div>}
      <div style={{ ...bodyStyle, display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px', overflow: 'hidden' }}>
        {/* Left: Configuration rail */}
        <div style={{ overflowY: 'auto', paddingRight: '8px' }}>
          <div style={cardStyle}>
            <label style={labelStyle}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} style={inputStyle} placeholder="Optional" />

            <label style={{ ...labelStyle, marginTop: '12px' }}>Dataset</label>
            <select value={datasetSlug} onChange={(e) => { setDatasetSlug(e.target.value); setColumns([]); setFilters([]); setOrderBy([]); }} style={inputStyle} disabled={isEditing}>
              {datasets.map(d => <option key={d.slug} value={d.slug}>{d.name}</option>)}
            </select>
            {isEditing && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>Dataset is locked once a report is created.</div>}

            <label style={{ ...labelStyle, marginTop: '12px' }}>Visualization</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
              {VIZ_TYPES.map(v => (
                <button
                  key={v.key}
                  onClick={() => setVizType(v.key)}
                  style={{
                    padding: '6px',
                    borderRadius: '5px',
                    border: vizType === v.key ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
                    background: vizType === v.key ? 'rgba(99,102,241,0.15)' : 'var(--bg-input)',
                    color: vizType === v.key ? 'var(--accent-color)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: vizType === v.key ? 600 : 400,
                  }}
                >{v.label}</button>
              ))}
            </div>

            {/* Pivot / heatmap: pick which dimension becomes column axis */}
            {(vizType === 'pivot' || vizType === 'heatmap') && (
              <>
                <label style={{ ...labelStyle, marginTop: '12px' }}>Pivot column</label>
                <select value={pivotColKey} onChange={e => setPivotColKey(e.target.value)} style={inputStyle}>
                  <option value="">— pick a dimension —</option>
                  {columns.filter(c => c.role === 'dimension').map((c, i) => (
                    <option key={i} value={c.key}>{c.label || c.key}</option>
                  ))}
                </select>
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px', lineHeight: 1.4 }}>
                  This dimension's distinct values become column headers; the remaining dimension(s) become row labels. Add ≥2 dimensions and 1 measure.
                </div>
              </>
            )}

            {/* KPI: comparison-period toggle */}
            {vizType === 'kpi' && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '12px', fontSize: '12px' }}>
                  <input type="checkbox" checked={compareEnabled} onChange={e => setCompareEnabled(e.target.checked)} />
                  Compare to previous period
                </label>
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px', lineHeight: 1.4 }}>
                  Requires a <code>between</code> filter on a date column. The window is shifted backward by the same span; KPI tile shows ▲▼ delta + percent.
                </div>
              </>
            )}

            {/* Gauge config */}
            {vizType === 'gauge' && (
              <div style={{ marginTop: '12px' }}>
                <label style={labelStyle}>Gauge thresholds</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                  {['min', 'max', 'target', 'warn_at', 'danger_at'].map(k => (
                    <input key={k} type="number" placeholder={k}
                      value={vizConfig?.gauge?.[k] ?? ''}
                      onChange={e => setVizConfig(c => ({ ...c, gauge: { ...(c.gauge || {}), [k]: e.target.value === '' ? null : Number(e.target.value) } }))}
                      style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  ))}
                </div>
              </div>
            )}

            {/* Conditional formatting (table + pivot) */}
            {(vizType === 'table' || vizType === 'pivot') && (
              <ConditionalFormattingEditor
                rules={vizConfig?.conditional_format || []}
                columnLabels={columns.map(c => c.label || c.key)}
                onChange={(rules) => setVizConfig(c => ({ ...c, conditional_format: rules }))}
              />
            )}
          </div>

          {/* Available columns */}
          <div style={{ ...cardStyle, marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Available fields</div>
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '8px', lineHeight: 1.4 }}>
              Click <b style={{ color: '#6366f1' }}>+ Dim</b> to group by a field (x-axis / category) or <b style={{ color: '#10b981' }}>+ Measure</b> to aggregate it (bar height / line value). Bar, line, pie & KPI charts need ≥1 dimension and ≥1 measure.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }}>
              {availableColumns.map(col => (
                <div key={col.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', background: 'var(--bg-input)', borderRadius: '4px', fontSize: '11px' }}>
                  <span title={col.description} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                    <span style={{ color: 'var(--text-primary)' }}>{col.label}</span>
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: 4 }}>({col.type})</span>
                  </span>
                  <span style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <button onClick={() => addColumn(col, 'dimension')} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.4)', color: '#6366f1', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }} title="Add as dimension (group-by)">+ Dim</button>
                    <button onClick={() => addColumn(col, 'measure')} style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', borderRadius: 3, padding: '2px 8px', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }} title="Add as measure (aggregate)">+ Σ Measure</button>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Configured columns */}
          <div style={{ ...cardStyle, marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Columns ({columns.length})</div>
            {columns.length === 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                No columns configured — empty query returns raw rows (table only).
                {(vizType === 'bar' || vizType === 'line' || vizType === 'pie' || vizType === 'kpi') && (
                  <div style={{ marginTop: '6px', padding: '6px 8px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, color: '#f59e0b' }}>
                    <b>{vizType.toUpperCase()}</b> needs at least 1 dimension and 1 measure. Add them from <i>Available fields</i> above.
                  </div>
                )}
              </div>
            )}
            {columns.length > 0 && !columns.some(c => c.role === 'measure') && (vizType === 'bar' || vizType === 'line' || vizType === 'pie' || vizType === 'kpi') && (
              <div style={{ marginBottom: '6px', padding: '6px 8px', fontSize: '11px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, color: '#f59e0b' }}>
                No measure yet — <b>{vizType}</b> chart will not render. Click <b style={{ color: '#10b981' }}>+ Σ Measure</b> on a numeric or id field above (e.g., count of <code>id</code>).
              </div>
            )}
            {columns.length > 0 && !columns.some(c => c.role === 'dimension') && (vizType === 'bar' || vizType === 'line' || vizType === 'pie') && (
              <div style={{ marginBottom: '6px', padding: '6px 8px', fontSize: '11px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, color: '#f59e0b' }}>
                No dimension yet — <b>{vizType}</b> chart needs a category to group by. Click <b style={{ color: '#6366f1' }}>+ Dim</b> on a field above (e.g., <code>status</code>).
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {columns.map((col, idx) => {
                const dsCol = availableColumns.find(c => c.key === col.key);
                return (
                  <div key={idx} style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '5px', background: 'var(--bg-input)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600 }}>
                        {col.role === 'measure' ? 'Σ ' : ''}{dsCol?.label || col.key}
                      </span>
                      <button onClick={() => removeColumn(idx)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }} title="Remove">×</button>
                    </div>
                    <input value={col.label} onChange={e => updateColumn(idx, { label: e.target.value })} placeholder="Display label" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                    {col.role === 'measure' && (
                      <select value={col.agg || 'count'} onChange={e => updateColumn(idx, { agg: e.target.value, label: `${e.target.value}(${col.key})` })} style={{ ...inputStyle, marginTop: '4px', fontSize: '11px', padding: '4px 6px' }}>
                        {AGG_OPTIONS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                      </select>
                    )}
                    {col.role === 'dimension' && (dsCol?.type === 'date' || dsCol?.type === 'datetime') && (
                      <select value={col.bucket || ''} onChange={e => updateColumn(idx, { bucket: e.target.value || undefined })} style={{ ...inputStyle, marginTop: '4px', fontSize: '11px', padding: '4px 6px' }}>
                        {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Filters */}
          <div style={{ ...cardStyle, marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>Filters ({filters.length})</div>
              <button onClick={addFilter} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 8px' }}>+ Add</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {filters.map((flt, idx) => (
                <div key={idx} style={{ padding: '6px', border: '1px solid var(--border-color)', borderRadius: '5px', background: 'var(--bg-input)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <select value={flt.key} onChange={e => updateFilter(idx, { key: e.target.value })} style={{ ...inputStyle, padding: '4px 6px', fontSize: '11px', flex: 2 }}>
                      {availableColumns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <select value={flt.op} onChange={e => updateFilter(idx, { op: e.target.value })} style={{ ...inputStyle, padding: '4px 6px', fontSize: '11px', flex: 1 }}>
                      {FILTER_OPS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                    <button onClick={() => removeFilter(idx)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>×</button>
                  </div>
                  {flt.op !== 'is_null' && flt.op !== 'is_not_null' && (
                    <input
                      value={Array.isArray(flt.value) ? flt.value.join(',') : (flt.value ?? '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (flt.op === 'in' || flt.op === 'not_in') {
                          updateFilter(idx, { value: v.split(',').map(s => s.trim()).filter(Boolean) });
                        } else {
                          updateFilter(idx, { value: v });
                        }
                      }}
                      placeholder={flt.op === 'in' || flt.op === 'not_in' ? 'a, b, c' : 'value'}
                      style={{ ...inputStyle, padding: '4px 6px', fontSize: '11px' }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Limit */}
          <div style={{ ...cardStyle, marginTop: '12px' }}>
            <label style={labelStyle}>Row limit</label>
            <input type="number" value={limit} min={1} max={100000} onChange={e => setLimit(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Right: Preview */}
        <div style={{ overflowY: 'auto' }}>
          <div style={{ ...cardStyle, minHeight: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Preview</span>
              {preview && (
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                  {preview.total_rows ?? preview.rows?.length ?? 0} rows · {preview.duration_ms ?? '–'}ms
                </span>
              )}
            </div>
            {previewLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
            ) : preview ? (
              <VizRender result={preview} vizType={vizType} vizConfig={vizConfig} />
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Click Preview to run a sample query.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard Editor (Phase 4 — drag/resize + global filters + tile types) ─
const GLOBAL_FILTER_KEYS = [
  // Common filterable columns across the bundled datasets. The backend silently
  // skips filters whose key isn't present in a given tile's dataset, so a single
  // bar safely fans out to every tile.
  { key: 'status',       label: 'Status' },
  { key: 'priority',     label: 'Priority' },
  { key: 'assignee_name',label: 'Assignee' },
  { key: 'team_id',      label: 'Team' },
  { key: 'is_open',      label: 'Open only', type: 'bool' },
];

const TILE_TYPE_OPTIONS = [
  { key: 'report',   label: 'Report tile' },
  { key: 'markdown', label: 'Markdown / text' },
  { key: 'section',  label: 'Section header' },
  { key: 'divider',  label: 'Divider' },
];

// Process Intelligence widget kinds (§12.64 P4). These render via direct API
// fetches in the tile component because the dashboard /run endpoint already
// returns their data inline.
const PI_WIDGET_KINDS = [
  { kind: 'pi_process_graph',    label: 'PI · Process Graph' },
  { kind: 'pi_variant_table',    label: 'PI · Variant Table' },
  { kind: 'pi_bottleneck_chart', label: 'PI · Bottleneck Chart' },
];

function DashboardEditor({ workspaceId, dashboard, reports, onClose, onSaved }) {
  const isEditing = !!dashboard;
  const [name, setName] = useState(dashboard?.name || '');
  const [description, setDescription] = useState(dashboard?.description || '');
  const [layout, setLayout] = useState(() => {
    const existing = dashboard?.layout_json || [];
    // Backfill tile_id on legacy tiles so drag/resize keying is stable.
    return existing.map((t, i) => ({
      tile_id: t.tile_id || t.report_id || `t${i + 1}`,
      type: t.type || (t.report_id ? 'report' : 'markdown'),
      ...t,
    }));
  });
  const [globalFilters, setGlobalFilters] = useState(dashboard?.global_filters_json || []);
  const [runtimeFilters, setRuntimeFilters] = useState([]);
  const [tilesData, setTilesData] = useState({});
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(true);
  const [selectedTile, setSelectedTile] = useState(null);

  const reportsById = useMemo(() => Object.fromEntries(reports.map(r => [r.id, r])), [reports]);
  const [piPickerOpen, setPiPickerOpen] = useState(false);

  const runAll = useCallback(async (filtersOverride) => {
    if (!isEditing) return;
    setRunning(true);
    try {
      const body = { runtime_filters: filtersOverride || runtimeFilters };
      const r = await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards/${dashboard.id}/run`, body, getAuthHeaders());
      setTilesData(r.data.tiles || {});
    } catch (e) {
      setError('Failed to run dashboard');
    } finally {
      setRunning(false);
    }
  }, [workspaceId, dashboard, isEditing, runtimeFilters]);

  useEffect(() => { if (isEditing) runAll(); /* eslint-disable-next-line */ }, [isEditing]);

  const newTileId = () => `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
  const nextY = () => layout.reduce((m, t) => Math.max(m, (t.y || 0) + (t.h || 1)), 0);

  const addReportTile = (reportId) => {
    setLayout(L => [...L, { tile_id: newTileId(), type: 'report', report_id: reportId, x: 0, y: nextY(), w: 6, h: 3 }]);
  };
  const addStaticTile = (type) => {
    const defaults = type === 'divider'
      ? { w: 12, h: 1, config: {} }
      : type === 'section'
      ? { w: 12, h: 1, config: { text: 'Section heading' } }
      : { w: 6, h: 2, config: { text: '*Click to edit*' } };
    setLayout(L => [...L, { tile_id: newTileId(), type, x: 0, y: nextY(), ...defaults }]);
  };
  const removeTile = (tileId) => setLayout(L => L.filter(t => t.tile_id !== tileId));
  const updateTile = (tileId, patch) => setLayout(L => L.map(t => t.tile_id === tileId ? { ...t, ...patch } : t));
  const addPIWidgetTile = (config) => {
    setLayout(L => [...L, { tile_id: newTileId(), type: 'pi_widget', x: 0, y: nextY(), w: 6, h: 4, config }]);
  };

  // Global / runtime filter handling
  const upsertRuntimeFilter = (key, value) => {
    setRuntimeFilters(F => {
      const without = F.filter(f => f.key !== key);
      if (value === '' || value == null) return without;
      // is_open is bool → cast; everything else is text-eq.
      const def = GLOBAL_FILTER_KEYS.find(g => g.key === key);
      const v = def?.type === 'bool' ? (value === 'true') : value;
      return [...without, { key, op: 'eq', value: v }];
    });
  };

  const handleApplyRuntimeFilters = () => runAll(runtimeFilters);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = {
        name: name.trim(),
        description: description || null,
        layout_json: layout,
        global_filters_json: globalFilters,
      };
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards/${dashboard.id}`, body, getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards`, body, getAuthHeaders());
      }
      onSaved();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onClose} style={btnSecondary}>← Back</button>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dashboard name" style={{ ...inputStyle, width: '300px', fontSize: '15px', fontWeight: 600 }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setEditMode(m => !m)} style={btnSecondary} title="Toggle edit mode">{editMode ? '👁 View' : '✎ Edit'}</button>
          {isEditing && <button onClick={() => runAll()} style={btnSecondary} disabled={running}>{running ? 'Running…' : '▶ Refresh'}</button>}
          <button onClick={handleSave} style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : (isEditing ? 'Save changes' : 'Create dashboard')}</button>
        </div>
      </div>
      {error && <div style={{ padding: '8px 20px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px' }}>{error}</div>}

      {/* Global filter bar — applies to all tiles whose dataset has the key. */}
      {isEditing && (
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Global filters</span>
          {GLOBAL_FILTER_KEYS.map(g => {
            const cur = runtimeFilters.find(f => f.key === g.key);
            if (g.type === 'bool') {
              return (
                <select key={g.key} value={cur ? String(cur.value) : ''} onChange={e => upsertRuntimeFilter(g.key, e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: '120px' }}>
                  <option value="">{g.label}: any</option>
                  <option value="true">{g.label}: yes</option>
                  <option value="false">{g.label}: no</option>
                </select>
              );
            }
            return (
              <input key={g.key} value={cur?.value || ''} placeholder={g.label}
                onChange={e => upsertRuntimeFilter(g.key, e.target.value)}
                style={{ ...inputStyle, width: 'auto', maxWidth: '160px' }} />
            );
          })}
          <button onClick={handleApplyRuntimeFilters} style={btnSecondary} disabled={running}>Apply</button>
          {runtimeFilters.length > 0 && (
            <button onClick={() => { setRuntimeFilters([]); runAll([]); }} style={{ ...btnSecondary, color: '#ef4444' }}>Clear</button>
          )}
        </div>
      )}

      <div style={bodyStyle}>
        {editMode && (
          <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" style={{ ...inputStyle, maxWidth: '360px' }} />
            <select onChange={(e) => { if (e.target.value) { addReportTile(e.target.value); e.target.value = ''; } }} style={{ ...inputStyle, maxWidth: '260px' }}>
              <option value="">+ Add report tile…</option>
              {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <button onClick={() => addStaticTile('markdown')} style={btnSecondary} title="Markdown / text">+ Text</button>
            <button onClick={() => addStaticTile('section')} style={btnSecondary} title="Section header">+ Section</button>
            <button onClick={() => addStaticTile('divider')} style={btnSecondary} title="Divider">+ Divider</button>
            <button onClick={() => setPiPickerOpen(true)} style={btnSecondary} title="Process Intelligence widget">+ PI Widget</button>
          </div>
        )}
        {piPickerOpen && (
          <PIWidgetPicker
            workspaceId={workspaceId}
            onClose={() => setPiPickerOpen(false)}
            onAdd={(cfg) => { addPIWidgetTile(cfg); setPiPickerOpen(false); }}
          />
        )}

        {layout.length === 0 ? (
          <div style={{ ...cardStyle, padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            No tiles yet. Pick a report from the dropdown above to add one.
          </div>
        ) : (
          <DashboardGrid
            layout={layout}
            tilesData={tilesData}
            reportsById={reportsById}
            editMode={editMode}
            isEditing={isEditing}
            selectedTile={selectedTile}
            setSelectedTile={setSelectedTile}
            updateTile={updateTile}
            removeTile={removeTile}
          />
        )}
      </div>
    </div>
  );
}

// ─── Dashboard Grid (drag/resize on a 12-col snap-grid) ────────────────────
function DashboardGrid({ layout, tilesData, reportsById, editMode, isEditing, selectedTile, setSelectedTile, updateTile, removeTile }) {
  const containerRef = useRef(null);
  const ROW_H = 90;       // px per grid row
  const ROW_GAP = 14;     // px gap between rows/cols
  const COLS = 12;

  // Compute total rows so the container has the right height.
  const totalRows = layout.reduce((m, t) => Math.max(m, (t.y || 0) + (t.h || 1)), 0) || 1;

  const startDrag = (e, tile, mode) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    const containerRect = containerRef.current.getBoundingClientRect();
    const colW = (containerRect.width - ROW_GAP * (COLS - 1)) / COLS;
    const startX = e.clientX;
    const startY = e.clientY;
    const start = { x: tile.x, y: tile.y, w: tile.w, h: tile.h };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const colDelta = Math.round(dx / (colW + ROW_GAP));
      const rowDelta = Math.round(dy / (ROW_H + ROW_GAP));
      if (mode === 'move') {
        const nx = Math.max(0, Math.min(COLS - start.w, start.x + colDelta));
        const ny = Math.max(0, start.y + rowDelta);
        updateTile(tile.tile_id, { x: nx, y: ny });
      } else if (mode === 'resize') {
        const nw = Math.max(2, Math.min(COLS - start.x, start.w + colDelta));
        const nh = Math.max(1, start.h + rowDelta);
        updateTile(tile.tile_id, { w: nw, h: nh });
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: `${totalRows * ROW_H + (totalRows - 1) * ROW_GAP}px`,
        background: editMode
          ? `repeating-linear-gradient(to right, transparent 0 calc(8.333% - 1px), rgba(255,255,255,0.04) calc(8.333% - 1px) 8.333%)`
          : 'transparent',
      }}>
      {layout.map((tile) => {
        const left = `calc((100% - ${ROW_GAP * (COLS - 1)}px) * ${tile.x / COLS} + ${tile.x * ROW_GAP}px)`;
        const width = `calc((100% - ${ROW_GAP * (COLS - 1)}px) * ${tile.w / COLS} + ${(tile.w - 1) * ROW_GAP}px)`;
        const top = (tile.y || 0) * (ROW_H + ROW_GAP);
        const height = (tile.h || 1) * ROW_H + ((tile.h || 1) - 1) * ROW_GAP;
        const isSelected = selectedTile === tile.tile_id;

        return (
          <div key={tile.tile_id}
            onClick={() => editMode && setSelectedTile(tile.tile_id)}
            style={{
              position: 'absolute',
              left,
              width,
              top: `${top}px`,
              height: `${height}px`,
              transition: 'box-shadow 0.15s',
              boxShadow: isSelected ? '0 0 0 2px var(--accent-color)' : 'none',
              borderRadius: '8px',
            }}>
            <DashboardTile
              tile={tile}
              tileResult={tilesData[tile.tile_id]}
              report={tile.report_id ? reportsById[tile.report_id] : null}
              editMode={editMode}
              isEditing={isEditing}
              onMoveStart={(e) => startDrag(e, tile, 'move')}
              onResizeStart={(e) => startDrag(e, tile, 'resize')}
              onRemove={() => removeTile(tile.tile_id)}
              onChange={(patch) => updateTile(tile.tile_id, patch)}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Single tile renderer (handles all tile types + drag handles) ──────────
function DashboardTile({ tile, tileResult, report, editMode, isEditing, onMoveStart, onResizeStart, onRemove, onChange }) {
  // Static (markdown / divider / section) tiles render their own static config.
  if (tile.type === 'divider') {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
        {editMode && <TileChrome onMoveStart={onMoveStart} onResizeStart={onResizeStart} onRemove={onRemove} title="Divider" />}
      </div>
    );
  }
  if (tile.type === 'section') {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', padding: '0 6px' }}>
        {editMode ? (
          <input value={tile.config?.text || ''} placeholder="Section heading"
            onChange={e => onChange({ config: { ...(tile.config || {}), text: e.target.value } })}
            style={{ ...inputStyle, fontSize: '15px', fontWeight: 700, border: 'none', background: 'transparent' }} />
        ) : (
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{tile.config?.text || 'Section'}</div>
        )}
        {editMode && <TileChrome onMoveStart={onMoveStart} onResizeStart={onResizeStart} onRemove={onRemove} title="Section" />}
      </div>
    );
  }
  if (tile.type === 'markdown') {
    return (
      <div style={{ ...cardStyle, position: 'relative', width: '100%', height: '100%', overflow: 'auto', padding: '12px 14px' }}>
        {editMode ? (
          <textarea value={tile.config?.text || ''}
            onChange={e => onChange({ config: { ...(tile.config || {}), text: e.target.value } })}
            placeholder="Write markdown here…"
            style={{ ...inputStyle, width: '100%', height: 'calc(100% - 22px)', resize: 'none', fontFamily: 'monospace', fontSize: '12px' }} />
        ) : (
          <div className="markdown-body" style={{ background: 'transparent', color: 'var(--text-primary)', fontSize: '13px' }}>
            <Markdown>{tile.config?.text || ''}</Markdown>
          </div>
        )}
        {editMode && <TileChrome onMoveStart={onMoveStart} onResizeStart={onResizeStart} onRemove={onRemove} title="Text" />}
      </div>
    );
  }
  if (tile.type === 'pi_widget') {
    const cfg = tile.config || {};
    const data = tileResult?.data;
    const titleLabel = (PI_WIDGET_KINDS.find(k => k.kind === cfg.kind)?.label || 'PI Widget')
      + (cfg.source_label ? ` — ${cfg.source_label}` : '');
    return (
      <div style={{ ...cardStyle, position: 'relative', width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '10px 12px' }}>
        <div onMouseDown={editMode ? onMoveStart : undefined}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', cursor: editMode ? 'move' : 'default', userSelect: 'none' }}>
          <div style={{ fontSize: '12px', fontWeight: 600 }}>{titleLabel}</div>
          {editMode && (
            <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
              style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>×</button>
          )}
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {tileResult?.error ? (
            <div style={{ padding: '8px', color: '#ef4444', fontSize: '11px' }}>{tileResult.error}</div>
          ) : data ? (
            <PIWidgetView kind={cfg.kind} data={data} />
          ) : (
            <div style={{ padding: '8px', color: 'var(--text-tertiary)', fontSize: '11px' }}>
              {isEditing ? 'Loading…' : 'Save the dashboard, then refresh to see data.'}
            </div>
          )}
        </div>
        {editMode && (
          <div onMouseDown={onResizeStart} title="Drag to resize"
            style={{ position: 'absolute', right: '2px', bottom: '2px', width: '14px', height: '14px', cursor: 'nwse-resize', background: 'linear-gradient(135deg, transparent 50%, var(--accent-color) 50%)', borderBottomRightRadius: '8px' }} />
        )}
      </div>
    );
  }

  // Default: report tile.
  return (
    <div style={{ ...cardStyle, position: 'relative', width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '10px 12px' }}>
      <div onMouseDown={editMode ? onMoveStart : undefined}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', cursor: editMode ? 'move' : 'default', userSelect: 'none' }}>
        <div style={{ fontSize: '12px', fontWeight: 600 }}>{report?.name || tileResult?.report_name || '(missing report)'}</div>
        {editMode && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>×</button>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tileResult && !tileResult.error ? (
          <VizRender result={tileResult}
            vizType={tileResult.viz_type || report?.viz_type || 'table'}
            vizConfig={tileResult.viz_config || report?.viz_config_json || {}} />
        ) : tileResult?.error ? (
          <div style={{ padding: '8px', color: '#ef4444', fontSize: '11px' }}>{tileResult.error}</div>
        ) : (
          <div style={{ padding: '8px', color: 'var(--text-tertiary)', fontSize: '11px' }}>
            {isEditing ? 'Loading…' : 'Save the dashboard, then refresh to see data.'}
          </div>
        )}
      </div>
      {editMode && (
        <div onMouseDown={onResizeStart}
          title="Drag to resize"
          style={{
            position: 'absolute', right: '2px', bottom: '2px', width: '14px', height: '14px',
            cursor: 'nwse-resize',
            background: 'linear-gradient(135deg, transparent 50%, var(--accent-color) 50%)',
            borderBottomRightRadius: '8px',
          }} />
      )}
    </div>
  );
}

// ─── Process Intelligence widget picker + render (§12.64 P4) ──────────────
function PIWidgetPicker({ workspaceId, onClose, onAdd }) {
  const [kind, setKind] = useState('pi_process_graph');
  const [sourceType, setSourceType] = useState('workflow_run');
  const [workflows, setWorkflows] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL}/api/workflows?workspace_id=${workspaceId}`, getAuthHeaders()).catch(() => ({ data: [] })),
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, getAuthHeaders()).catch(() => ({ data: [] })),
    ]).then(([w, c]) => {
      setWorkflows(Array.isArray(w.data) ? w.data : (w.data?.workflows || []));
      setCaseTypes(Array.isArray(c.data) ? c.data : (c.data?.case_types || []));
    }).finally(() => setLoading(false));
  }, [workspaceId]);

  const sourceList = sourceType === 'workflow_run' ? workflows : caseTypes;
  const selectedSource = sourceList.find(s => s.id === sourceId);

  const handleAdd = () => {
    if (!sourceId) { setError('Pick a source'); return; }
    onAdd({
      kind,
      source_type: sourceType,
      source_id: sourceId,
      source_label: selectedSource?.name || '',
      options: kind === 'pi_variant_table' ? { top_k: 10 } : (kind === 'pi_bottleneck_chart' ? { top_n: 10 } : {}),
    });
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ ...cardStyle, padding: '20px 24px', minWidth: '460px', maxWidth: '560px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Add Process Intelligence widget</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', fontSize: '18px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <label style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Widget kind
            <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
              {PI_WIDGET_KINDS.map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Source type
            <select value={sourceType} onChange={(e) => { setSourceType(e.target.value); setSourceId(''); }} style={{ ...inputStyle, width: '100%' }}>
              <option value="workflow_run">Workflow</option>
              <option value="case">Case type</option>
            </select>
          </label>
          <label style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Source
            {loading ? (
              <div style={{ padding: '8px', color: 'var(--text-tertiary)', fontSize: '12px' }}>Loading…</div>
            ) : sourceList.length === 0 ? (
              <div style={{ padding: '8px', color: 'var(--text-tertiary)', fontSize: '12px' }}>No {sourceType === 'workflow_run' ? 'workflows' : 'case types'} in this workspace.</div>
            ) : (
              <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value="">— pick one —</option>
                {sourceList.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
              </select>
            )}
          </label>
        </div>
        {error && <div style={{ marginTop: '10px', color: '#ef4444', fontSize: '12px' }}>{error}</div>}
        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleAdd} style={btnPrimary} disabled={!sourceId}>Add widget</button>
        </div>
      </div>
    </div>
  );
}

function PIWidgetView({ kind, data }) {
  if (kind === 'pi_process_graph') {
    const g = data?.graph || {};
    const nodes = g.nodes || [];
    const edges = g.edges || [];
    const stats = g.stats || {};
    return (
      <div style={{ fontSize: '12px' }}>
        <div style={{ display: 'flex', gap: '14px', marginBottom: '8px' }}>
          <Stat label="Nodes" value={nodes.length} />
          <Stat label="Edges" value={edges.length} />
          <Stat label="Events" value={stats.event_count ?? '—'} />
        </div>
        <div style={{ maxHeight: '160px', overflow: 'auto', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
          {nodes.slice(0, 20).map((n) => (
            <div key={n.id} style={{ padding: '2px 4px', display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
              <span style={{ color: 'var(--text-primary)' }}>{n.label || n.id}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>{n.count ?? ''}</span>
            </div>
          ))}
          {nodes.length > 20 && <div style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>… +{nodes.length - 20} more</div>}
        </div>
      </div>
    );
  }
  if (kind === 'pi_variant_table') {
    const vs = data?.variants || [];
    return (
      <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: 'var(--bg-tertiary)' }}>
          <th style={{ padding: '4px 6px', textAlign: 'left' }}>Path</th>
          <th style={{ padding: '4px 6px', textAlign: 'right' }}>Count</th>
          <th style={{ padding: '4px 6px', textAlign: 'right' }}>Share</th>
          <th style={{ padding: '4px 6px', textAlign: 'right' }}>p95 (s)</th>
        </tr></thead>
        <tbody>
          {vs.map((v) => (
            <tr key={v.path_hash}>
              <td style={{ padding: '3px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>{(v.path || []).join(' → ')}</td>
              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{v.count}</td>
              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{v.share != null ? `${(v.share * 100).toFixed(1)}%` : '—'}</td>
              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{v.p95_duration_s != null ? v.p95_duration_s.toFixed(2) : '—'}</td>
            </tr>
          ))}
          {vs.length === 0 && <tr><td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No variants yet</td></tr>}
        </tbody>
      </table>
    );
  }
  if (kind === 'pi_bottleneck_chart') {
    const bs = data?.bottlenecks || [];
    const maxScore = bs.reduce((m, b) => Math.max(m, b.score ?? 0), 0) || 1;
    return (
      <div style={{ fontSize: '11px' }}>
        {bs.map((b, i) => (
          <div key={`${b.step_key}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 4px' }}>
            <div style={{ width: '40%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-primary)' }}>{b.step_key}</div>
            <div style={{ flex: 1, height: '10px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${((b.score ?? 0) / maxScore) * 100}%`, height: '100%', background: '#f59e0b' }} />
            </div>
            <div style={{ width: '48px', textAlign: 'right', color: 'var(--text-tertiary)' }}>{(b.score ?? 0).toFixed(2)}</div>
          </div>
        ))}
        {bs.length === 0 && <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No bottlenecks ranked</div>}
      </div>
    );
  }
  return <div style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>Unknown PI widget kind: {kind}</div>;
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function TileChrome({ onMoveStart, onResizeStart, onRemove, title }) {
  return (
    <>
      <div onMouseDown={onMoveStart} title={`Drag ${title || 'tile'}`}
        style={{ position: 'absolute', top: '2px', left: '2px', padding: '2px 6px', fontSize: '10px', color: 'var(--text-tertiary)', cursor: 'move', background: 'var(--bg-tertiary)', borderRadius: '4px', userSelect: 'none' }}>⋮⋮</div>
      <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
        style={{ position: 'absolute', top: '2px', right: '4px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>×</button>
      <div onMouseDown={onResizeStart} title="Drag to resize"
        style={{ position: 'absolute', right: '2px', bottom: '2px', width: '14px', height: '14px', cursor: 'nwse-resize', background: 'linear-gradient(135deg, transparent 50%, var(--accent-color) 50%)', borderBottomRightRadius: '6px' }} />
    </>
  );
}

// ─── Alert modal ───────────────────────────────────────────────────────────
function AlertModal({ workspaceId, report, alert, onClose, onSaved, resultColumns }) {
  const isEditing = !!alert;
  const measureCols = (resultColumns || []).filter(c => c.role === 'measure');
  const [metricPath, setMetricPath] = useState(alert?.metric_path || (measureCols[0] ? `sum:${measureCols[0].label}` : 'count:rows'));
  const [op, setOp] = useState(alert?.condition?.op || 'gt');
  const [threshold, setThreshold] = useState(alert?.condition?.threshold ?? 0);
  const [actionFmt, setActionFmt] = useState(alert?.action?.format || 'csv');
  const [channels, setChannels] = useState(alert?.action?.channels || [{ type: 'in_app', config: {} }]);
  const [emails, setEmails] = useState((alert?.action?.channels || []).find(c => c.type === 'email')?.config?.recipients?.join(', ') || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const channelList = channels.map(c => c.type === 'email' ? { type: 'email', config: { recipients: emails.split(',').map(s => s.trim()).filter(Boolean) } } : c);
      const body = {
        metric_path: metricPath,
        condition_json: { op, threshold: Number(threshold) },
        window_minutes: 5,
        action_json: { format: actionFmt, channels: channelList },
        is_active: true,
      };
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/alerts/${alert.id}`, body, getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${report.id}/alerts`, body, getAuthHeaders());
      }
      onSaved();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleChannel = (type) => {
    setChannels(c => c.some(ch => ch.type === type) ? c.filter(ch => ch.type !== type) : [...c, { type, config: {} }]);
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>{isEditing ? 'Edit alert' : 'New alert'}</div>
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '6px 10px', borderRadius: 4, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}

        <label style={labelStyle}>Metric</label>
        <input value={metricPath} onChange={e => setMetricPath(e.target.value)} style={inputStyle} placeholder="e.g. sum:count(id) or count:rows" />
        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          Forms: <code>count:rows</code>, <code>sum:&lt;col&gt;</code>, <code>max:&lt;col&gt;</code>, <code>min:&lt;col&gt;</code>, <code>avg:&lt;col&gt;</code>, <code>first:&lt;col&gt;</code>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
          <div>
            <label style={labelStyle}>Operator</label>
            <select value={op} onChange={e => setOp(e.target.value)} style={inputStyle}>
              <option value="gt">&gt;</option>
              <option value="gte">≥</option>
              <option value="lt">&lt;</option>
              <option value="lte">≤</option>
              <option value="eq">=</option>
              <option value="ne">≠</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Threshold</label>
            <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <label style={{ ...labelStyle, marginTop: '10px' }}>When firing, send via</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {['in_app', 'email', 'slack'].map(t => {
            const active = channels.some(c => c.type === t);
            return (
              <button key={t} onClick={() => toggleChannel(t)} style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${active ? 'var(--accent-color)' : 'var(--border-color)'}`, background: active ? 'rgba(99,102,241,0.15)' : 'var(--bg-input)', color: active ? 'var(--accent-color)' : 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer' }}>{t}</button>
            );
          })}
        </div>
        {channels.some(c => c.type === 'email') && (
          <input value={emails} onChange={e => setEmails(e.target.value)} style={{ ...inputStyle, marginTop: '6px', fontSize: '12px' }} placeholder="alice@example.com, bob@example.com" />
        )}

        <label style={{ ...labelStyle, marginTop: '10px' }}>Format for delivery payload</label>
        <select value={actionFmt} onChange={e => setActionFmt(e.target.value)} style={inputStyle}>
          <option value="csv">CSV</option>
          <option value="xlsx">Excel</option>
          <option value="html">HTML</option>
        </select>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : (isEditing ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Report Viewer (run-only) ──────────────────────────────────────────────
function ReportViewer({ workspaceId, report, onEdit, onClose }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [editingAlert, setEditingAlert] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [expandedEventsAlertId, setExpandedEventsAlertId] = useState(null);
  const [eventsByAlert, setEventsByAlert] = useState({});

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${report.id}/run`, {}, getAuthHeaders());
      setResult(r.data);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Run failed');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, report]);

  useEffect(() => { run(); }, [run]);

  const loadAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${report.id}/alerts`, getAuthHeaders());
      setAlerts(r.data || []);
    } catch (e) { /* swallow */ }
  }, [workspaceId, report.id]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleDeleteAlert = async (id) => {
    if (!confirm('Delete this alert?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/alerts/${id}`, getAuthHeaders());
      loadAlerts();
    } catch (e) { setError('Delete failed'); }
  };

  const handleEvaluateAlert = async (id) => {
    try {
      const r = await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/alerts/${id}/evaluate`, {}, getAuthHeaders());
      alert(`Alert state: ${r.data.state}\nMetric: ${r.data.metric ?? '—'}\nThreshold: ${r.data.threshold}\nTransitioned: ${r.data.transitioned}`);
      loadAlerts();
      // Refresh the events panel if it's open for this alert.
      if (expandedEventsAlertId === id) loadAlertEvents(id);
    } catch (e) {
      setError('Evaluate failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const loadAlertEvents = async (id) => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/alerts/${id}/events?limit=20`, getAuthHeaders());
      setEventsByAlert(prev => ({ ...prev, [id]: r.data || [] }));
    } catch (e) {
      setError('Failed to load alert events: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const toggleEvents = (id) => {
    if (expandedEventsAlertId === id) {
      setExpandedEventsAlertId(null);
    } else {
      setExpandedEventsAlertId(id);
      if (!eventsByAlert[id]) loadAlertEvents(id);
    }
  };

  const copyAlertId = (id) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(id);
    }
  };

  const handleExport = (fmt) => {
    const url = `${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${report.id}/export?fmt=${fmt}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) return Promise.reject(r.statusText);
        const degraded = r.headers.get('X-Report-Degraded');
        const cd = r.headers.get('Content-Disposition') || '';
        const m = /filename="?([^"]+)"?/i.exec(cd);
        const filename = m ? m[1] : `${report.name}.${fmt}`;
        return r.blob().then(blob => ({ blob, filename, degraded }));
      })
      .then(({ blob, filename, degraded }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        if (degraded) {
          setError(`Export degraded: ${degraded}. Downloaded as HTML — install Playwright on the server for ${fmt.toUpperCase()} output.`);
        }
      })
      .catch(err => setError(`Export failed: ${err}`));
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onClose} style={btnSecondary}>← Back</button>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600 }}>{report.name}</div>
            {report.description && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{report.description}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={run} style={btnSecondary} disabled={loading}>{loading ? 'Running…' : '↻ Refresh'}</button>
          <button onClick={() => setShowAlerts(s => !s)} style={{ ...btnSecondary, position: 'relative' }} title="Alerts">
            🔔 Alerts{alerts.length > 0 ? ` (${alerts.length})` : ''}
            {alerts.some(a => a.last_state === 'firing') && (
              <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: 10, height: 10, background: '#ef4444', borderRadius: '50%' }} />
            )}
          </button>
          <select onChange={(e) => { if (e.target.value) { handleExport(e.target.value); e.target.value = ''; } }} defaultValue="" style={{ ...inputStyle, width: '120px', padding: '8px 10px' }}>
            <option value="">⬇ Export…</option>
            <option value="csv">CSV</option>
            <option value="xlsx">Excel (.xlsx)</option>
            <option value="html">HTML</option>
            <option value="pdf">PDF</option>
            <option value="png">PNG snapshot</option>
          </select>
          <button onClick={onEdit} style={btnPrimary}>Edit</button>
        </div>
      </div>
      {error && <div style={{ padding: '8px 20px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px' }}>{error}</div>}
      <div style={bodyStyle}>
        {showAlerts && (
          <div style={{ ...cardStyle, marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600 }}>🔔 Alerts on this report</div>
              <button onClick={() => setEditingAlert('new')} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 10px' }}>+ New alert</button>
            </div>
            {alerts.length === 0 ? (
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>No alerts. Create one to get notified when a metric crosses a threshold (evaluated every 5 min).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {alerts.map(a => (
                  <div key={a.id} style={{ padding: '8px 10px', background: 'var(--bg-input)', borderRadius: 5, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '12px' }}>
                          <code style={{ color: 'var(--accent-color)' }}>{a.metric_path}</code>
                          <span style={{ margin: '0 6px', color: 'var(--text-secondary)' }}>{a.condition?.op}</span>
                          <code>{a.condition?.threshold}</code>
                          <span style={{ marginLeft: '10px', fontSize: '10px', padding: '1px 6px', borderRadius: 8,
                            background: a.last_state === 'firing' ? 'rgba(239,68,68,0.15)' : a.last_state === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                            color: a.last_state === 'firing' ? '#ef4444' : a.last_state === 'ok' ? '#22c55e' : '#94a3b8' }}>
                            {a.last_state || 'pending'}
                          </span>
                        </div>
                        {a.last_fired_at && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: 2 }}>Last fired: {new Date(a.last_fired_at).toLocaleString()}</div>}
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: 2 }}>
                          ID: <code style={{ fontSize: '10px' }}>{a.id}</code>
                          <button onClick={() => copyAlertId(a.id)} title="Copy alert ID for use in a report_alert_trigger workflow node" style={{ ...btnSecondary, fontSize: '9px', padding: '1px 5px', marginLeft: 6 }}>Copy</button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => toggleEvents(a.id)} style={{ ...btnSecondary, fontSize: '10px', padding: '3px 7px' }}>{expandedEventsAlertId === a.id ? '▾ Hide events' : '▸ Events'}</button>
                        <button onClick={() => handleEvaluateAlert(a.id)} style={{ ...btnSecondary, fontSize: '10px', padding: '3px 7px' }}>▶ Evaluate</button>
                        <button onClick={() => setEditingAlert(a)} style={{ ...btnSecondary, fontSize: '10px', padding: '3px 7px' }}>Edit</button>
                        <button onClick={() => handleDeleteAlert(a.id)} style={{ ...btnDanger, fontSize: '10px', padding: '3px 7px' }}>×</button>
                      </div>
                    </div>
                    {expandedEventsAlertId === a.id && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: 4 }}>Recent events</div>
                        {eventsByAlert[a.id] === undefined ? (
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Loading…</div>
                        ) : eventsByAlert[a.id].length === 0 ? (
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>No fire/recovery events recorded yet.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 220, overflowY: 'auto' }}>
                            {eventsByAlert[a.id].map(ev => (
                              <div key={ev.id} style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--bg-card)', borderRadius: 4, display: 'grid', gridTemplateColumns: '90px 1fr', columnGap: 8 }}>
                                <span style={{
                                  fontSize: '10px',
                                  padding: '1px 5px',
                                  borderRadius: 6,
                                  alignSelf: 'start',
                                  background: ev.state === 'firing' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                  color: ev.state === 'firing' ? '#ef4444' : '#22c55e',
                                }}>{ev.state}</span>
                                <div>
                                  <div style={{ color: 'var(--text-secondary)' }}>{new Date(ev.fired_at).toLocaleString()} · metric: <code>{ev.metric_value ?? '—'}</code></div>
                                  {Array.isArray(ev.action_results) && ev.action_results.length > 0 && (
                                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: 1 }}>
                                      Channels: {ev.action_results.map((ar, i) => (
                                        <span key={i} style={{ marginRight: 6, color: ar.ok ? '#22c55e' : '#ef4444' }}>
                                          {ar.channel}{ar.ok ? ' ✓' : ' ✗'}{ar.attempts > 1 ? ` (${ar.attempts}x)` : ''}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {Array.isArray(ev.payload?.triggered_workflows) && ev.payload.triggered_workflows.length > 0 && (
                                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: 1 }}>
                                      Triggered {ev.payload.triggered_workflows.length} workflow{ev.payload.triggered_workflows.length === 1 ? '' : 's'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ ...cardStyle, minHeight: '400px' }}>
          {result && (
            <div style={{ marginBottom: '12px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
              {result.total_rows ?? result.rows?.length ?? 0} rows · {result.duration_ms ?? '–'}ms · dataset: {report.dataset_slug}
            </div>
          )}
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
          ) : result ? (
            <VizRender result={result} vizType={report.viz_type || 'table'} vizConfig={report.viz_config_json || {}} />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No data.</div>
          )}
        </div>
      </div>
      {editingAlert && (
        <AlertModal
          workspaceId={workspaceId}
          report={report}
          alert={editingAlert === 'new' ? null : editingAlert}
          resultColumns={result?.columns}
          onClose={() => setEditingAlert(null)}
          onSaved={() => { setEditingAlert(null); loadAlerts(); }}
        />
      )}
    </div>
  );
}

// ─── Schedule editor modal ─────────────────────────────────────────────────
const COMMON_CRONS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Daily at 9am', value: '0 9 * * *' },
  { label: 'Weekdays at 9am', value: '0 9 * * 1-5' },
  { label: 'Mondays at 8am', value: '0 8 * * 1' },
  { label: '1st of month at 8am', value: '0 8 1 * *' },
];

function ScheduleModal({ workspaceId, schedule, reports, dashboards, audiences, onClose, onSaved }) {
  const isEditing = !!schedule;
  const [targetType, setTargetType] = useState(schedule?.target_type || 'report');
  const [targetId, setTargetId] = useState(schedule?.target_id || '');
  const [cron, setCron] = useState(schedule?.cron || '0 9 * * 1-5');
  const [timezone, setTz] = useState(schedule?.timezone || 'UTC');
  const [format, setFormat] = useState(schedule?.format || 'csv');
  const [audienceId, setAudienceId] = useState(schedule?.audience_id || '');
  const [channels, setChannels] = useState(schedule?.delivery_channels || []);
  const [isActive, setIsActive] = useState(schedule?.is_active !== false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const targetList = targetType === 'report' ? reports : dashboards;

  const addChannel = (type) => {
    const defaults = {
      email: { type: 'email', config: { recipients: [], subject: '' } },
      slack: { type: 'slack', config: { webhook_url: '' } },
      teams: { type: 'teams', config: { webhook_url: '' } },
      webhook: { type: 'webhook', config: { url: '' } },
      s3: { type: 's3', config: { bucket: '', key: '{ts}/{filename}', region: 'us-east-1' } },
      in_app: { type: 'in_app', config: {} },
    };
    setChannels(c => [...c, defaults[type]]);
  };
  const removeChannel = (idx) => setChannels(c => c.filter((_, i) => i !== idx));
  const updateChannel = (idx, patch) => setChannels(c => c.map((ch, i) => i === idx ? { ...ch, config: { ...ch.config, ...patch } } : ch));

  const handleSave = async () => {
    if (!targetId) { setError('Pick a target report or dashboard'); return; }
    if (!cron || cron.split(/\s+/).length < 5) { setError('cron must have 5+ fields, e.g. "0 9 * * 1-5"'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        target_type: targetType, target_id: targetId,
        cron, timezone, format,
        delivery_channels_json: channels,
        audience_id: audienceId || null,
        is_active: isActive,
      };
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/schedules/${schedule.id}`,
          { cron, timezone, format, delivery_channels_json: channels, audience_id: audienceId || null, is_active: isActive },
          getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/schedules`, body, getAuthHeaders());
      }
      onSaved();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, width: '640px' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>{isEditing ? 'Edit schedule' : 'New schedule'}</div>
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '6px 10px', borderRadius: 4, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Target type</label>
            <select value={targetType} onChange={e => { setTargetType(e.target.value); setTargetId(''); }} style={inputStyle} disabled={isEditing}>
              <option value="report">Report</option>
              <option value="dashboard">Dashboard</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Target</label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)} style={inputStyle} disabled={isEditing}>
              <option value="">— pick one —</option>
              {targetList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: '10px' }}>
          <label style={labelStyle}>Cron</label>
          <input value={cron} onChange={e => setCron(e.target.value)} style={inputStyle} placeholder="0 9 * * 1-5" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
            {COMMON_CRONS.map(c => (
              <button key={c.value} onClick={() => setCron(c.value)} style={{ ...btnSecondary, fontSize: '10px', padding: '3px 7px' }}>{c.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '10px' }}>
          <div>
            <label style={labelStyle}>Timezone</label>
            <input value={timezone} onChange={e => setTz(e.target.value)} style={inputStyle} placeholder="UTC" />
          </div>
          <div>
            <label style={labelStyle}>Format</label>
            <select value={format} onChange={e => setFormat(e.target.value)} style={inputStyle}>
              <option value="csv">CSV</option>
              <option value="xlsx">Excel</option>
              <option value="html">HTML</option>
              <option value="pdf">PDF (Playwright)</option>
              <option value="png">PNG snapshot</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Audience</label>
            <select value={audienceId} onChange={e => setAudienceId(e.target.value)} style={inputStyle}>
              <option value="">— none —</option>
              {audiences.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <label style={labelStyle}>Delivery channels ({channels.length})</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={() => addChannel('email')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ Email</button>
              <button onClick={() => addChannel('slack')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ Slack</button>
              <button onClick={() => addChannel('teams')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ Teams</button>
              <button onClick={() => addChannel('webhook')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ Webhook</button>
              <button onClick={() => addChannel('s3')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ S3</button>
              <button onClick={() => addChannel('in_app')} style={{ ...btnSecondary, fontSize: '11px', padding: '3px 8px' }}>+ In-app</button>
            </div>
          </div>
          {channels.map((ch, idx) => (
            <div key={idx} style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: 5, marginTop: 6, background: 'var(--bg-input)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600 }}>{ch.type}</span>
                <button onClick={() => removeChannel(idx)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
              </div>
              {ch.type === 'email' && (
                <>
                  <input value={(ch.config.recipients || []).join(', ')} onChange={e => updateChannel(idx, { recipients: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                    placeholder="alice@example.com, bob@example.com" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  <input value={ch.config.subject || ''} onChange={e => updateChannel(idx, { subject: e.target.value })}
                    placeholder="Subject (optional)" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px', marginTop: '4px' }} />
                </>
              )}
              {ch.type === 'slack' && (
                <input value={ch.config.webhook_url || ''} onChange={e => updateChannel(idx, { webhook_url: e.target.value })}
                  placeholder="https://hooks.slack.com/services/…" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
              )}
              {ch.type === 'teams' && (
                <input value={ch.config.webhook_url || ''} onChange={e => updateChannel(idx, { webhook_url: e.target.value })}
                  placeholder="https://outlook.office.com/webhook/…" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
              )}
              {ch.type === 'webhook' && (
                <input value={ch.config.url || ''} onChange={e => updateChannel(idx, { url: e.target.value })}
                  placeholder="https://your-server.example.com/hook" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
              )}
              {ch.type === 's3' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                  <input value={ch.config.bucket || ''} onChange={e => updateChannel(idx, { bucket: e.target.value })}
                    placeholder="bucket name" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  <input value={ch.config.region || ''} onChange={e => updateChannel(idx, { region: e.target.value })}
                    placeholder="us-east-1" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  <input value={ch.config.key || ''} onChange={e => updateChannel(idx, { key: e.target.value })}
                    placeholder="{ts}/{filename}" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px', gridColumn: 'span 2' }} />
                  <input value={ch.config.aws_access_key_id || ''} onChange={e => updateChannel(idx, { aws_access_key_id: e.target.value })}
                    placeholder="AWS access key (or env)" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  <input type="password" value={ch.config.aws_secret_access_key || ''} onChange={e => updateChannel(idx, { aws_secret_access_key: e.target.value })}
                    placeholder="AWS secret (or env)" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px' }} />
                  <input value={ch.config.endpoint_url || ''} onChange={e => updateChannel(idx, { endpoint_url: e.target.value })}
                    placeholder="custom endpoint (MinIO/R2/…) — optional" style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px', gridColumn: 'span 2' }} />
                </div>
              )}
              {ch.type === 'in_app' && (
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>Posts to all workspace members' notification center.</div>
              )}
            </div>
          ))}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '12px' }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          Active
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : (isEditing ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Audience editor modal ─────────────────────────────────────────────────
function AudienceModal({ workspaceId, audience, onClose, onSaved }) {
  const isEditing = !!audience;
  const [name, setName] = useState(audience?.name || '');
  const [emails, setEmails] = useState(((audience?.members || []).filter(m => m.type === 'email').map(m => m.ref)).join(', '));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name required'); return; }
    setSaving(true); setError('');
    try {
      const members = emails.split(',').map(s => s.trim()).filter(Boolean).map(e => ({ type: 'email', ref: e }));
      const body = { name: name.trim(), members_json: members };
      if (isEditing) {
        await axios.patch(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/audiences/${audience.id}`, body, getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/audiences`, body, getAuthHeaders());
      }
      onSaved();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>{isEditing ? 'Edit audience' : 'New audience'}</div>
        {error && <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '6px 10px', borderRadius: 4, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
        <label style={labelStyle}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="Ops team" />
        <label style={{ ...labelStyle, marginTop: '10px' }}>Email recipients (comma-separated)</label>
        <textarea value={emails} onChange={e => setEmails(e.target.value)} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
          placeholder="alice@example.com, bob@example.com" />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : (isEditing ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Schedules tab ─────────────────────────────────────────────────────────
function SchedulesView({ workspaceId, reports, dashboards }) {
  const [schedules, setSchedules] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);  // null | 'new' | scheduleObj
  const [editingAudience, setEditingAudience] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [sRes, aRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/schedules`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/audiences`, getAuthHeaders()),
      ]);
      setSchedules(sRes.data || []);
      setAudiences(aRes.data || []);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to load');
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const targetName = (s) => {
    const list = s.target_type === 'report' ? reports : dashboards;
    return list.find(x => x.id === s.target_id)?.name || '(missing)';
  };

  const runNow = async (id) => {
    if (!confirm('Run this schedule now and dispatch to all configured channels?')) return;
    try {
      await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/schedules/${id}/run-now`, {}, getAuthHeaders());
      reload();
    } catch (e) {
      setError('Run failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/schedules/${id}`, getAuthHeaders());
      reload();
    } catch (e) { setError('Delete failed'); }
  };

  const handleDeleteAudience = async (id) => {
    if (!confirm('Delete this audience? Schedules referencing it will keep their reference but resolve to no recipients.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/audiences/${id}`, getAuthHeaders());
      reload();
    } catch (e) { setError('Delete failed'); }
  };

  return (
    <div>
      {error && <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: 4, marginBottom: '12px' }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>Audiences ({audiences.length})</div>
        <button onClick={() => setEditingAudience('new')} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 10px' }}>+ New audience</button>
      </div>
      {audiences.length === 0 ? (
        <div style={{ ...cardStyle, fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '20px' }}>No audiences yet — create one to broadcast scheduled reports to a list of emails.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px', marginBottom: '20px' }}>
          {audiences.map(a => (
            <div key={a.id} style={{ ...cardStyle, padding: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{a.name}</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => setEditingAudience(a)} style={{ ...btnSecondary, fontSize: '10px', padding: '2px 6px' }}>Edit</button>
                  <button onClick={() => handleDeleteAudience(a.id)} style={{ ...btnDanger, fontSize: '10px', padding: '2px 6px' }}>×</button>
                </div>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>{(a.members || []).length} member(s)</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>Schedules ({schedules.length})</div>
        <button onClick={() => setEditing('new')} style={btnPrimary} disabled={reports.length + dashboards.length === 0}>+ New schedule</button>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
      ) : schedules.length === 0 ? (
        <div style={{ ...cardStyle, padding: '32px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
          <div style={{ fontSize: '36px' }}>⏱</div>
          <div style={{ fontSize: '14px', fontWeight: 600, marginTop: '8px', color: 'var(--text-primary)' }}>No schedules yet</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>Schedule a report to be delivered automatically by cron, in any of CSV/Excel/HTML/PDF.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {schedules.map(s => (
            <div key={s.id} style={{ ...cardStyle, padding: '12px', display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                  <span style={{ background: s.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)', color: s.is_active ? '#22c55e' : '#94a3b8', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: 8 }}>{s.is_active ? 'ACTIVE' : 'PAUSED'}</span>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>{targetName(s)}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>· {s.target_type}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <code style={{ background: 'var(--bg-input)', padding: '1px 6px', borderRadius: 3 }}>{s.cron}</code>
                  <span style={{ marginLeft: '8px' }}>{s.timezone}</span>
                  <span style={{ marginLeft: '8px' }}>· {s.format}</span>
                  {(s.delivery_channels || []).length > 0 && <span style={{ marginLeft: '8px' }}>· {s.delivery_channels.length} channel(s)</span>}
                  {s.audience_id && <span style={{ marginLeft: '8px' }}>· audience: {audiences.find(a => a.id === s.audience_id)?.name || '?'}</span>}
                </div>
                {s.last_run_at && (
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                    Last run: {new Date(s.last_run_at).toLocaleString()} · {s.last_status || '?'}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => runNow(s.id)} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 8px' }}>▶ Run now</button>
                <button onClick={() => setEditing(s)} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 8px' }}>Edit</button>
                <button onClick={() => handleDelete(s.id)} style={{ ...btnDanger, fontSize: '11px', padding: '4px 8px' }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ScheduleModal
          workspaceId={workspaceId}
          schedule={editing === 'new' ? null : editing}
          reports={reports}
          dashboards={dashboards}
          audiences={audiences}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {editingAudience && (
        <AudienceModal
          workspaceId={workspaceId}
          audience={editingAudience === 'new' ? null : editingAudience}
          onClose={() => setEditingAudience(null)}
          onSaved={() => { setEditingAudience(null); reload(); }}
        />
      )}
    </div>
  );
}

// ─── Snapshots tab ─────────────────────────────────────────────────────────
function SnapshotsView({ workspaceId, reports, dashboards }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/snapshots?limit=200`, getAuthHeaders());
      setSnapshots(r.data || []);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to load');
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const targetName = (s) => {
    const list = s.target_type === 'report' ? reports : dashboards;
    return list.find(x => x.id === s.target_id)?.name || '(deleted)';
  };

  return (
    <div>
      {error && <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px', borderRadius: 4, marginBottom: '12px' }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>Run history ({snapshots.length})</div>
        <button onClick={reload} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 10px' }}>↻ Refresh</button>
      </div>
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
      ) : snapshots.length === 0 ? (
        <div style={{ ...cardStyle, padding: '32px', textAlign: 'center', color: 'var(--text-tertiary)' }}>No snapshots yet — every run / export / scheduled delivery records one.</div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: 'var(--bg-input)' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>When</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Target</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Format</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Rows</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Duration</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{s.ran_at ? new Date(s.ran_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent-color)', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: 8, marginRight: 6 }}>{s.target_type}</span>
                    {targetName(s)}
                  </td>
                  <td style={{ padding: '6px 10px', color: 'var(--text-secondary)' }}>{s.file_format || '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.row_count ?? '—'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-tertiary)' }}>{s.duration_ms != null ? `${s.duration_ms}ms` : '—'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ color: s.status === 'ok' ? '#22c55e' : '#ef4444', fontSize: '11px' }}>
                      {s.status}{s.error_text ? ` — ${s.error_text.slice(0, 60)}` : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function Reports({ workspaceId, onBack }) {
  const [tab, setTab] = useState('reports');  // reports | dashboards | schedules | snapshots | datasets | embed | templates
  const [datasets, setDatasets] = useState([]);
  const [reports, setReports] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');   // list | report_view | report_edit | report_create | dash_edit | dash_create
  const [activeReport, setActiveReport] = useState(null);
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [licenseError, setLicenseError] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError('');
    setLicenseError(null);
    try {
      const [dsRes, repRes, dashRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/datasets`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards`, getAuthHeaders()),
      ]);
      setDatasets(dsRes.data.datasets || []);
      setReports(repRes.data || []);
      setDashboards(dashRes.data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === 'feature_locked') {
        setLicenseError(detail);
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to load reports');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const handleDeleteReport = async (id) => {
    if (!confirm('Delete this report? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/reports/${id}`, getAuthHeaders());
      reload();
    } catch (e) {
      setError('Delete failed');
    }
  };

  const handleDeleteDashboard = async (id) => {
    if (!confirm('Delete this dashboard?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards/${id}`, getAuthHeaders());
      reload();
    } catch (e) {
      setError('Delete failed');
    }
  };

  const [seeding, setSeeding] = useState(false);
  const handleSeedDefaults = async () => {
    if (!confirm('Seed the five first-party dashboards (Casematrix Ops, SLA Health, Workflow Reliability, My Team, Forms Throughput)?\n\nExisting dashboards/reports with the same name are left untouched.')) return;
    setSeeding(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/api/reports/workspaces/${workspaceId}/dashboards/seed-defaults`, {}, getAuthHeaders());
      const created = r.data.created_dashboards || [];
      const skipped = r.data.skipped_dashboards || [];
      const reportsCreated = (r.data.created_reports || []).length;
      alert(`Seeded ${created.length} dashboard(s) + ${reportsCreated} report(s).` + (skipped.length ? `\nSkipped (already existed): ${skipped.join(', ')}` : ''));
      reload();
    } catch (e) {
      setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  // ─── License lock screen ────────────────────────────────────────────────
  if (licenseError) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          {onBack && <button onClick={onBack} style={btnSecondary}>← Back</button>}
          <div style={{ fontSize: '15px', fontWeight: 600 }}>Reports & Dashboards</div>
          <div style={{ width: '70px' }} />
        </div>
        <div style={bodyStyle}>
          <div style={{ ...cardStyle, maxWidth: '520px', margin: '60px auto', textAlign: 'center', padding: '32px' }}>
            <div style={{ fontSize: '40px' }}>🔒</div>
            <div style={{ fontSize: '17px', fontWeight: 600, marginTop: '8px' }}>{licenseError.label}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '6px' }}>{licenseError.message}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '14px' }}>Current plan: <b>{licenseError.plan}</b></div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Sub-views (editor/viewer) ──────────────────────────────────────────
  if (view === 'report_create') {
    return <ReportEditor workspaceId={workspaceId} datasets={datasets} report={null}
      onClose={() => setView('list')}
      onSaved={() => { setView('list'); reload(); }} />;
  }
  if (view === 'report_edit' && activeReport) {
    return <ReportEditor workspaceId={workspaceId} datasets={datasets} report={activeReport}
      onClose={() => setView('list')}
      onSaved={() => { setView('list'); reload(); }} />;
  }
  if (view === 'report_view' && activeReport) {
    return <ReportViewer workspaceId={workspaceId} report={activeReport}
      onEdit={() => setView('report_edit')}
      onClose={() => setView('list')} />;
  }
  if (view === 'dash_create') {
    return <DashboardEditor workspaceId={workspaceId} dashboard={null} reports={reports}
      onClose={() => setView('list')}
      onSaved={() => { setView('list'); reload(); }} />;
  }
  if (view === 'dash_edit' && activeDashboard) {
    return <DashboardEditor workspaceId={workspaceId} dashboard={activeDashboard} reports={reports}
      onClose={() => setView('list')}
      onSaved={() => { setView('list'); reload(); }} />;
  }

  // ─── List view ──────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {onBack && <button onClick={onBack} style={btnSecondary}>← Back</button>}
          <div style={{ fontSize: '15px', fontWeight: 600 }}>📊 Reports & Dashboards</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {tab === 'reports' && <button onClick={() => { setActiveReport(null); setView('report_create'); }} style={btnPrimary}>+ New report</button>}
          {tab === 'dashboards' && <button onClick={handleSeedDefaults} style={btnSecondary} disabled={seeding} title="Create the five first-party dashboards">{seeding ? 'Seeding…' : '✨ Seed defaults'}</button>}
          {tab === 'dashboards' && <button onClick={() => { setActiveDashboard(null); setView('dash_create'); }} style={btnPrimary} disabled={reports.length === 0}>+ New dashboard</button>}
          {(tab === 'schedules' || tab === 'snapshots') && <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', alignSelf: 'center' }}>Phase 5 · Scheduled delivery & run history</span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', padding: '0 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <button onClick={() => setTab('reports')} style={tabStyle(tab === 'reports')}>Reports ({reports.length})</button>
        <button onClick={() => setTab('dashboards')} style={tabStyle(tab === 'dashboards')}>Dashboards ({dashboards.length})</button>
        <button onClick={() => setTab('schedules')} style={tabStyle(tab === 'schedules')}>Schedules</button>
        <button onClick={() => setTab('snapshots')} style={tabStyle(tab === 'snapshots')}>Run history</button>
        <button onClick={() => setTab('datasets')} style={tabStyle(tab === 'datasets')}>Datasets</button>
        <button onClick={() => setTab('embed')} style={tabStyle(tab === 'embed')}>Embed tokens</button>
        <button onClick={() => setTab('templates')} style={tabStyle(tab === 'templates')}>Templates</button>
      </div>

      {error && <div style={{ padding: '8px 20px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '12px' }}>{error}</div>}

      <div style={bodyStyle}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
        ) : tab === 'schedules' ? (
          <SchedulesView workspaceId={workspaceId} reports={reports} dashboards={dashboards} />
        ) : tab === 'snapshots' ? (
          <SnapshotsView workspaceId={workspaceId} reports={reports} dashboards={dashboards} />
        ) : tab === 'datasets' ? (
          <DatasetsView workspaceId={workspaceId} />
        ) : tab === 'embed' ? (
          <EmbedTokensView workspaceId={workspaceId} reports={reports} />
        ) : tab === 'templates' ? (
          <TemplatesView workspaceId={workspaceId} onApplied={() => reload()} />
        ) : tab === 'reports' ? (
          reports.length === 0 ? (
            <div style={{ ...cardStyle, maxWidth: '520px', margin: '40px auto', textAlign: 'center', padding: '32px' }}>
              <div style={{ fontSize: '36px' }}>📊</div>
              <div style={{ fontSize: '15px', fontWeight: 600, marginTop: '8px' }}>No reports yet</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Create your first report on top of a built-in dataset (cases, work items, workflow runs, etc.).</div>
              <div style={{ marginTop: '14px' }}><button onClick={() => { setActiveReport(null); setView('report_create'); }} style={btnPrimary}>+ New report</button></div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {reports.map(r => (
                <div key={r.id} style={{ ...cardStyle, cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={() => { setActiveReport(r); setView('report_view'); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{r.name}</div>
                      {r.description && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{r.description}</div>}
                    </div>
                    <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent-color)', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px' }}>{r.viz_type || 'table'}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '8px' }}>Dataset: <b>{r.dataset_slug}</b></div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>Updated: {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                    <button onClick={(e) => { e.stopPropagation(); setActiveReport(r); setView('report_edit'); }} style={{ ...btnSecondary, fontSize: '11px', padding: '4px 8px' }}>Edit</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteReport(r.id); }} style={{ ...btnDanger, fontSize: '11px', padding: '4px 8px' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          dashboards.length === 0 ? (
            <div style={{ ...cardStyle, maxWidth: '520px', margin: '40px auto', textAlign: 'center', padding: '32px' }}>
              <div style={{ fontSize: '36px' }}>📈</div>
              <div style={{ fontSize: '15px', fontWeight: 600, marginTop: '8px' }}>No dashboards yet</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{reports.length === 0 ? 'Create at least one report first — dashboards compose existing reports as tiles.' : 'Compose existing reports into a tile grid.'}</div>
              {reports.length > 0 && <div style={{ marginTop: '14px' }}><button onClick={() => { setActiveDashboard(null); setView('dash_create'); }} style={btnPrimary}>+ New dashboard</button></div>}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {dashboards.map(d => (
                <div key={d.id} style={{ ...cardStyle, cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={() => { setActiveDashboard(d); setView('dash_edit'); }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{d.name}</div>
                  {d.description && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{d.description}</div>}
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '8px' }}>{(d.layout_json || []).length} tile(s)</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>Updated: {d.updated_at ? new Date(d.updated_at).toLocaleString() : '—'}</div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteDashboard(d.id); }} style={{ ...btnDanger, fontSize: '11px', padding: '4px 8px' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
